/**
 * Severity classification manager
 * Uses Strategy pattern to classify error severity
 */

import type { SeverityStrategy, ErrorContext } from './SeverityStrategy.js';
import type { Severity } from '../../types.js';
import { JavaScriptErrorSeverity } from './JavaScriptErrorSeverity.js';
import { NetworkErrorSeverity } from './NetworkErrorSeverity.js';
import { ResourceErrorSeverity } from './ResourceErrorSeverity.js';
import { PromiseRejectionSeverity } from './PromiseRejectionSeverity.js';

export class SeverityClassifier {
  private strategies: SeverityStrategy[];

  constructor(customStrategies: SeverityStrategy[] = []) {
    // Default strategies
    this.strategies = [
      new JavaScriptErrorSeverity(),
      new NetworkErrorSeverity(),
      new ResourceErrorSeverity(),
      new PromiseRejectionSeverity(),
      ...customStrategies
    ];
  }

  /**
   * Classify error severity using appropriate strategy
   */
  classify(context: ErrorContext): Severity {
    for (const strategy of this.strategies) {
      if (strategy.canHandle(context)) {
        return strategy.classify(context);
      }
    }

    // Default to medium if no strategy handles it
    return 'medium';
  }

  /**
   * Add a custom strategy
   */
  addStrategy(strategy: SeverityStrategy): void {
    this.strategies.push(strategy);
  }
}

// Export types and classes
export type { SeverityStrategy, ErrorContext };
export { JavaScriptErrorSeverity, NetworkErrorSeverity, ResourceErrorSeverity, PromiseRejectionSeverity };

